/**
 * #473 — o painel de conclusão do código da Tabela 24, aberto pela listagem de riscos.
 *
 * A tarefa é uma só: fechar de uma vez o código do eSocial dos riscos marcados na lista,
 * aceitando o que o catálogo sugere ou dizendo que o risco não é agente nocivo. Por isso a
 * gravação é uma chamada só no fim, e não uma por linha — meio lote gravado deixaria o
 * mutirão sem saber o que já foi analisado.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { Block as BlockIcon } from '@mui/icons-material';

import ESocialReferenceAutocomplete from '../../companies/components/esocial/forms/ESocialReferenceAutocomplete';
import { hazardousAgentService, type HazardousAgentSheet } from '../../../services/hazardousAgentService';
import { esocialReferenceService } from '../../../services/esocialReferenceService';
import { occupationalRiskService } from '../../../services/occupationalRiskService';
import type { EsocialAgentCodeBulkEntry, OccupationalRisk } from '../../../types/occupationalRisk';

const TABELA_AGENTES_NOCIVOS = 24;

/**
 * Artigos, preposições e as palavras genéricas do domínio, que aparecem em quase toda
 * descrição e por isso não confirmam nada. Espelho da lista do backend.
 */
const PALAVRAS_SEM_VALOR = new Set([
  'agente', 'agentes', 'trabalho', 'trabalhos', 'exposicao', 'exposicoes',
  'nivel', 'niveis', 'acima', 'a', 'o', 'as', 'os', 'um', 'uma', 'uns', 'umas',
  'de', 'da', 'do', 'das', 'dos', 'e', 'ou', 'em', 'no', 'na', 'nos', 'nas',
  'com', 'sem', 'por', 'pelo', 'pela', 'para', 'ao', 'aos', 'que', 'se',
  'seu', 'seus', 'sua', 'suas', 'outro', 'outros', 'outra', 'outras',
]);

const semAcento = (texto: string): string =>
  texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const palavrasSignificativas = (texto: string): Set<string> =>
  new Set(
    semAcento(texto)
      .split(/[^a-z0-9]+/)
      .filter((palavra) => palavra.length >= 2 && !PALAVRAS_SEM_VALOR.has(palavra))
  );

/**
 * A conferência da D0, no navegador: um código só é aceito **sozinho** quando o nome do
 * risco tem palavra significativa em comum com a descrição oficial do código. Ser válido na
 * Tabela 24 não é prova de que está certo para aquele risco.
 *
 * A regra canônica é do backend (`tabela24CodeMatch`), mas ela roda na migration de dados —
 * o endpoint de gravação em lote não a executa, porque ali quem escolhe é uma pessoa. Este
 * é o único ponto do fluxo do painel em que a máquina escolhe, então a conferência tem de
 * acontecer aqui. Divergir do backend custa, no máximo, uma sugestão a mais para conferir à
 * mão; nunca uma gravação errada.
 */
function sugestaoConfirmada(
  nomesDoRisco: ReadonlyArray<string | null | undefined>,
  descricaoOficial: string
): boolean {
  const nomeCompleto = nomesDoRisco.filter((nome): nome is string => !!nome).join(' ');
  const palavrasDoNome = palavrasSignificativas(nomeCompleto);
  if (palavrasDoNome.size === 0) return false;

  for (const palavra of palavrasSignificativas(descricaoOficial)) {
    if (palavrasDoNome.has(palavra)) return true;
  }
  return false;
}

interface LinhaDoPainel {
  riscoId: string;
  nome: string;
  agente: string | null;
  sugestao: string | null;
  descricaoDaSugestao: string | null;
  confirmada: boolean;
  codigo: string;
  naoSeAplica: boolean;
  /**
   * O campo de busca guarda o texto escolhido em estado próprio, e não reage a mudança de
   * `value` vinda de fora. Trocar a chave é o que faz o campo voltar a refletir a linha
   * quando o código é aplicado ou limpo por um botão daqui.
   */
  versao: number;
}

interface PainelCodigoEsocialProps {
  aberto: boolean;
  riscos: OccupationalRisk[];
  onFechar: () => void;
  onGravado: () => void;
}

const plural = (quantidade: number, singular: string, plural: string): string =>
  quantidade === 1 ? singular : plural;

export const PainelCodigoEsocial: React.FC<PainelCodigoEsocialProps> = ({
  aberto,
  riscos,
  onFechar,
  onGravado,
}) => {
  const [linhas, setLinhas] = useState<LinhaDoPainel[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [catalogoIndisponivel, setCatalogoIndisponivel] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto) return;

    let cancelado = false;
    setCarregando(true);
    setErro(null);
    setCatalogoIndisponivel(false);

    montarLinhas(riscos).then(({ linhas: montadas, catalogoFalhou }) => {
      if (cancelado) return;
      setLinhas(montadas);
      setCatalogoIndisponivel(catalogoFalhou);
      setCarregando(false);
    });

    return () => {
      cancelado = true;
    };
  }, [aberto, riscos]);

  const alterarLinha = useCallback(
    (riscoId: string, mudanca: Partial<LinhaDoPainel>) =>
      setLinhas((atuais) =>
        atuais.map((linha) => (linha.riscoId === riscoId ? { ...linha, ...mudanca } : linha))
      ),
    []
  );

  const escolherCodigo = (linha: LinhaDoPainel, codigo: string) =>
    alterarLinha(linha.riscoId, { codigo, naoSeAplica: codigo ? false : linha.naoSeAplica });

  const usarSugestao = (linha: LinhaDoPainel) =>
    alterarLinha(linha.riscoId, {
      codigo: linha.sugestao ?? '',
      naoSeAplica: false,
      versao: linha.versao + 1,
    });

  const alternarNaoSeAplica = (linha: LinhaDoPainel) =>
    alterarLinha(linha.riscoId, {
      naoSeAplica: !linha.naoSeAplica,
      codigo: '',
      versao: linha.versao + 1,
    });

  const aplicarSugestoesConfirmadas = () =>
    setLinhas((atuais) =>
      atuais.map((linha) =>
        linha.confirmada && !linha.codigo && !linha.naoSeAplica
          ? { ...linha, codigo: linha.sugestao ?? '', versao: linha.versao + 1 }
          : linha
      )
    );

  const itensParaGravar = useMemo<EsocialAgentCodeBulkEntry[]>(
    () =>
      linhas.flatMap((linha): EsocialAgentCodeBulkEntry[] => {
        if (linha.naoSeAplica) return [{ id: Number(linha.riscoId), naoSeAplica: true }];
        if (linha.codigo) return [{ id: Number(linha.riscoId), esocialAgentCode: linha.codigo }];
        return [];
      }),
    [linhas]
  );

  const sugestoesAplicadas = linhas.filter((linha) => linha.confirmada).length;
  const sugestoesAConferir = linhas.filter((linha) => linha.sugestao && !linha.confirmada).length;
  const temSugestao = sugestoesAplicadas + sugestoesAConferir > 0;
  const podeReaplicar = linhas.some(
    (linha) => linha.confirmada && !linha.codigo && !linha.naoSeAplica
  );

  const resumoDasSugestoes =
    `${sugestoesAplicadas} ${plural(sugestoesAplicadas, 'sugestão aplicada', 'sugestões aplicadas')}` +
    ` do catálogo · ${sugestoesAConferir} a conferir` +
    (sugestoesAConferir > 0 ? ', porque o nome do risco não confirma o código sugerido' : '');

  const gravar = async () => {
    setGravando(true);
    setErro(null);
    try {
      await occupationalRiskService.updateEsocialAgentCodeBulk(itensParaGravar);
      onGravado();
    } catch (err: unknown) {
      const falha = err as { message?: string };
      setErro(falha.message ?? 'Não foi possível gravar os códigos.');
    } finally {
      setGravando(false);
    }
  };

  return (
    <Dialog open={aberto} onClose={onFechar} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Código do agente nocivo (Tabela 24)
        <Typography variant="body2" color="text.secondary">
          {`${riscos.length} ${plural(riscos.length, 'risco selecionado', 'riscos selecionados')} · a gravação acontece uma vez só, no fim`}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {erro && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {`Nada foi gravado. ${erro}`}
          </Alert>
        )}

        {catalogoIndisponivel && !carregando && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Não foi possível consultar o catálogo de agentes agora, então nenhuma sugestão foi
            carregada. A busca na Tabela 24 continua funcionando em cada linha.
          </Alert>
        )}

        {carregando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {temSugestao && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  mb: 3,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {resumoDasSugestoes}
                </Typography>
                <Button size="small" onClick={aplicarSugestoesConfirmadas} disabled={!podeReaplicar}>
                  Aplicar todas as sugestões
                </Button>
              </Box>
            )}

            <Stack divider={<Divider flexItem />} spacing={3}>
              {linhas.map((linha) => (
                <Box key={linha.riscoId}>
                  <Typography variant="subtitle2">{linha.nome}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {linha.agente ?? 'Sem agente do catálogo'}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1 }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <ESocialReferenceAutocomplete
                        key={`${linha.riscoId}:${linha.versao}`}
                        tableNumber={TABELA_AGENTES_NOCIVOS}
                        value={linha.codigo}
                        onChange={(codigo) => escolherCodigo(linha, codigo)}
                        label="Código da Tabela 24"
                        disabled={linha.naoSeAplica}
                        size="small"
                      />
                    </Box>
                    <Button
                      size="small"
                      variant={linha.naoSeAplica ? 'contained' : 'outlined'}
                      color={linha.naoSeAplica ? 'primary' : 'inherit'}
                      startIcon={<BlockIcon />}
                      aria-pressed={linha.naoSeAplica}
                      onClick={() => alternarNaoSeAplica(linha)}
                    >
                      Não se aplica
                    </Button>
                  </Box>

                  {linha.sugestao && !linha.confirmada && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Typography variant="caption" color="warning.main">
                        {`Sugestão a conferir: ${linha.sugestao}${
                          linha.descricaoDaSugestao ? ` — ${linha.descricaoDaSugestao}` : ''
                        }`}
                      </Typography>
                      <Button size="small" onClick={() => usarSugestao(linha)}>
                        Usar
                      </Button>
                    </Box>
                  )}

                  {linha.confirmada && linha.codigo === linha.sugestao && !linha.naoSeAplica && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="success"
                      label="Sugestão do catálogo"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              ))}
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onFechar} disabled={gravando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={gravar}
          disabled={gravando || itensParaGravar.length === 0}
        >
          {`Gravar ${itensParaGravar.length} ${plural(itensParaGravar.length, 'risco', 'riscos')}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

async function montarLinhas(
  riscos: OccupationalRisk[]
): Promise<{ linhas: LinhaDoPainel[]; catalogoFalhou: boolean }> {
  // O id chega como string do backend, embora o tipo diga `number`: sem normalizar, o mapa
  // das fichas não encontra nada e toda linha aparece sem agente.
  const idsDosAgentes = [
    ...new Set(riscos.map((risco) => risco.hazardousAgentId).filter(Boolean).map(String)),
  ];

  const fichas = new Map<string, HazardousAgentSheet>();
  const oficiais = new Map<string, string>();
  let catalogoFalhou = false;

  try {
    const carregadas = await Promise.all(
      idsDosAgentes.map((id) => hazardousAgentService.getSheet(id))
    );
    carregadas.forEach((ficha, indice) => fichas.set(idsDosAgentes[indice]!, ficha));

    const codigos = [
      ...new Set(
        [...fichas.values()]
          .map((ficha) => ficha.esocialCode)
          .filter((codigo): codigo is string => Boolean(codigo))
      ),
    ];

    if (codigos.length > 0) {
      const itens = await esocialReferenceService.lookup(TABELA_AGENTES_NOCIVOS, codigos);
      itens.forEach((item) => oficiais.set(item.code, item.description));
    }
  } catch {
    catalogoFalhou = true;
  }

  const linhas = riscos.map((risco) => {
    const ficha = risco.hazardousAgentId ? fichas.get(String(risco.hazardousAgentId)) : undefined;
    const sugestao = ficha?.esocialCode ?? null;
    const descricaoDaSugestao = sugestao ? oficiais.get(sugestao) ?? null : null;
    const confirmada = Boolean(
      sugestao &&
        descricaoDaSugestao &&
        sugestaoConfirmada([risco.hazard, risco.riskFactorName], descricaoDaSugestao)
    );

    return {
      riscoId: String(risco.id),
      nome: risco.riskFactorName,
      agente: ficha?.displayName ?? null,
      sugestao,
      descricaoDaSugestao,
      confirmada,
      codigo: confirmada && sugestao ? sugestao : '',
      naoSeAplica: false,
      versao: 0,
    };
  });

  return { linhas, catalogoFalhou };
}
